import { type ZenrinMapRequestHistoryResponse } from "../../types";
import {
  type GridColDef,
  type GridRenderCellParams,
  type GridSelectionModel,
  type GridValueGetterParams,
} from "@mui/x-data-grid-pro";
import { StyledDataGrid } from "@/components/DataGrid/ChibanKaokuNumberSearchStyleDataGrid";
import { format, parseISO } from "date-fns";
import { LatLng, type Map as LeafletMap } from "leaflet";
import { useEffect, useMemo, useRef, useState } from "react";

type ZenrinRequestHistoryRow =
  ZenrinMapRequestHistoryResponse["results"][number];

interface ZenrinRequestHistoryFeedGridProps {
  rows: ZenrinRequestHistoryRow[];
  map: LeafletMap | null;
  selectedIds: GridSelectionModel;
  onSelectionModelChange: (newSelectionModel: GridSelectionModel) => void;
  page: number;
  setPage: React.Dispatch<React.SetStateAction<number>>;
  pageSize: number;
  setPageSize: React.Dispatch<React.SetStateAction<number>>;
}

// 住所列の最小幅
const ADDRESS_MIN_WIDTH = 130;

export const ZenrinRequestHistoryFeedGrid: React.FC<
  ZenrinRequestHistoryFeedGridProps
> = ({
  rows,
  map,
  selectedIds,
  onSelectionModelChange,
  page,
  setPage,
  pageSize,
  setPageSize,
}) => {
  const [addressWidth, setAddressWidth] = useState<number>(260);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  // 文字幅を計算する
  const measureTextPx = (text: string): number => {
    if (!canvasRef.current) {
      canvasRef.current = document.createElement("canvas");
    }
    const ctx = canvasRef.current.getContext("2d");
    if (!ctx) return text.length * 10;

    ctx.font =
      (typeof window !== "undefined" && document?.body
        ? getComputedStyle(document.body).font
        : null) ?? "14px Roboto, Helvetica, Arial, sans-serif";
    return ctx.measureText(text).width;
  };

  // 住所の幅を自動調整する
  useEffect(() => {
    // ヘッダーも含めて「表示中のrows」から最大幅を推定して住所列のwidthを更新
    const header = "住所";
    const candidates = [header, ...rows.map((r) => String(r.address ?? ""))];

    const maxTextPx = candidates.reduce((max, s) => {
      const px = measureTextPx(s);
      return px > max ? px : max;
    }, 0);

    // DataGridのセル内余白ぶんを足す
    const next = Math.max(ADDRESS_MIN_WIDTH, Math.ceil(maxTextPx + 32));

    setAddressWidth(next);
  }, [rows]);

  const handlePageChange = (newPage: number): void => {
    setPage(newPage);
  };

  const handlePageSizeChange = (newPageSize: number): void => {
    setPageSize(newPageSize);
  };

  const formatDate = (dateString: string): string => {
    try {
      const date = parseISO(dateString);
      return format(date, "yyyy/MM/dd HH時mm分");
    } catch (e) {
      console.error("日付のフォーマットに失敗しました:", dateString, e);
      return dateString;
    }
  };

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: "address",
        headerName: "住所",
        width: addressWidth,
        minWidth: ADDRESS_MIN_WIDTH,
        sortable: true,
        align: "left",
        headerAlign: "left",
      },
      {
        field: "distanceKm",
        headerName: "距離",
        width: 100,
        sortable: true,
        align: "center",
        headerAlign: "center",
        valueGetter: (
          params: GridValueGetterParams<
            Pick<ZenrinMapRequestHistoryResponse, "results">
          >
        ) => {
          // 現在表示している位置からの距離をkmで計算する
          if (!map) return null;

          const { centerLatitude, centerLongitude } = params.row;
          if (
            typeof centerLatitude !== "number" ||
            typeof centerLongitude !== "number"
          ) {
            return null;
          }
          const mapCenter = map.getCenter();
          const distanceMeters = mapCenter.distanceTo(
            new LatLng(centerLatitude, centerLongitude)
          );
          return Number(distanceMeters / 1000);
        },
        renderCell: (params: GridRenderCellParams<number>) => {
          const value = params.value;
          return typeof value === "number" ? `${value.toFixed(1)} km` : "";
        },
      },
      {
        field: "status",
        headerName: "ステータス",
        width: 120,
        sortable: true,
        align: "center",
        headerAlign: "center",
        valueGetter: (params: GridValueGetterParams<string>) => {
          const status = params.value;
          if (status === "succeeded") return "成功";
          if (status === "failed") return "失敗";
          return status;
        },
      },
      {
        field: "createdAt",
        headerName: "作成日時",
        width: 170,
        sortable: true,
        align: "center",
        headerAlign: "center",
        renderCell: (params: GridRenderCellParams) => {
          if (params.value && typeof params.value === "string") {
            return formatDate(params.value);
          }
          return "";
        },
      },
    ],
    [addressWidth, map]
  );

  return (
    <StyledDataGrid
      disableColumnMenu
      rows={rows}
      columns={columns}
      checkboxSelection
      selectionModel={selectedIds}
      page={page}
      pageSize={pageSize}
      onPageChange={handlePageChange}
      onPageSizeChange={handlePageSizeChange}
      pagination
      rowsPerPageOptions={[25, 50, 100]}
      initialState={{
        sorting: {
          sortModel: [{ field: "distanceKm", sort: "asc" }],
        },
      }}
      onSelectionModelChange={onSelectionModelChange}
      getRowHeight={() => "auto"}
      sx={{
        "& .MuiDataGrid-cell": {
          py: 1,
        },
      }}
    />
  );
};
